import React from 'react';
import { css, cx } from '@emotion/css';
import { useNavigate } from 'react-router-dom';
import { FaInstagram, FaLinkedin, FaWhatsapp } from 'react-icons/fa';
import AnchorExperiencesLogomark from '../../../assets/anchor-experiences-brand/anchor-experiences-logomark.svg';

export interface FooterProps extends React.HTMLAttributes<HTMLElement> {}

const wrapper = css({
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  backgroundColor: 'var(--g-100)',
  borderTop: '1px solid var(--pc-100)',
});

const container = css({
  width: '100%',
  maxWidth: '1440px',
  padding: 'var(--spacing-2xl) var(--spacing-sm)',
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--spacing-xl)',
});

const firstSection = css({
  display: 'flex',
  justifyContent: 'space-around',
  flexWrap: 'wrap',
  gap: 'var(--spacing-xl)',
  '@media (max-width: 768px)': {
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    gap: 'var(--spacing-lg)',
  },
});

const logo = css({
  width: '100px',
  cursor: 'pointer',
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
  },
});

const menu = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--spacing-sm)',
  '@media (max-width: 768px)': {
    width: '100%',
  },
});

const menuTitle = css({
  fontWeight: 700,
  fontSize: 'var(--fs-400)',
  color: 'var(--pc-700)',
});

const menuList = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--spacing-3xs)',
  listStyle: 'none',
  padding: 0,
  margin: 0,
});

const menuItem = css({
  fontSize: 'var(--fs-350)',
  cursor: 'pointer',
  color: 'var(--g-600)',
  opacity: 0.7,
  transition: 'opacity 0.3s ease',
  position: 'relative',
  display: 'inline-block',
  '&:hover': {
    opacity: 1,
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    width: '100%',
    transform: 'scaleX(0)',
    height: '1px',
    bottom: 0,
    left: 0,
    backgroundColor: 'var(--pc-500)',
    transformOrigin: 'bottom right',
    transition: 'transform 0.3s ease',
  },
  '&:hover::after': {
    transform: 'scaleX(1)',
    transformOrigin: 'bottom left',
  },
});

const divider = css({
  width: '100%',
  height: '1px',
  backgroundColor: 'var(--pc-200)',
});

const secondSection = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 'var(--spacing-sm)',
});

const socialMediaTitle = css({
  fontWeight: 700,
  fontSize: 'var(--fs-400)',
  color: 'var(--pc-700)',
});

const socialIcons = css({
  display: 'flex',
  gap: 'var(--spacing-lg)',
  alignItems: 'center',
});

const socialIcon = css({
  color: 'var(--pc-400)',
  fontSize: '22px',
  cursor: 'pointer',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    color: 'var(--pc-600)',
    transform: 'translateY(-2px)',
  },
});

const copyright = css({
  fontSize: 'var(--fs-300)',
  color: 'var(--g-500)',
});

export const Footer: React.FC<FooterProps> = ({ className, ...rest }) => {
  const navigate = useNavigate();

  const handleNavigate = (path: string) => {
    navigate(path);
  };

  return (
    <footer className={cx(wrapper, className)} {...rest}>
      <div className={container}>
        <div className={firstSection}>
          <div className={logo} onClick={() => window.open('https://anchorexperiences.com.br/')}>
            <img src={AnchorExperiencesLogomark} alt="Anchor Experiences" />
          </div>
          <div className={menu}>
            <p className={menuTitle}>Políticas</p>
            <ul className={menuList}>
              <li>
                <p className={menuItem} onClick={() => handleNavigate('/politicas/half-price-policy')}>
                  Políticas de meia-entrada
                </p>
              </li>
              <li>
                <p className={menuItem} onClick={() => handleNavigate('/politicas/privacy-policy')}>
                  Políticas de privacidade
                </p>
              </li>
              <li>
                <p className={menuItem} onClick={() => handleNavigate('/politicas/refund-policy')}>
                  Políticas de reembolso
                </p>
              </li>
              <li>
                <p className={menuItem} onClick={() => handleNavigate('/politicas/terms-of-use')}>
                  Termos e condições
                </p>
              </li>
            </ul>
          </div>
          <div className={menu}>
            <p className={menuTitle}>Comercial</p>
            <ul className={menuList}>
              <li>
                <p className={menuItem} onClick={() => handleNavigate('/comercial/patrocinador')}>
                  Seja um patrocinador
                </p>
              </li>
              <li>
                <p className={menuItem} onClick={() => handleNavigate('/comercial/vender-evento')}>
                  Venda seu evento
                </p>
              </li>
              <li>
                <p className={menuItem} onClick={() => handleNavigate('/comercial/vendas-grupos')}>
                  Venda a grupos
                </p>
              </li>
            </ul>
          </div>
          <div className={menu}>
            <p className={menuTitle}>Ajuda</p>
            <ul className={menuList}>
              <li>
                <p className={menuItem} onClick={() => handleNavigate('/contato')}>
                  Entre em contato
                </p>
              </li>
            </ul>
          </div>
        </div>
        <div className={divider} />
        <div className={secondSection}>
          <p className={socialMediaTitle}>Redes Sociais</p>
          <div className={socialIcons}>
            <FaInstagram
              className={socialIcon}
              onClick={() => window.open('https://www.instagram.com/anchorexperiences')}
            />
            <FaWhatsapp
              className={socialIcon}
              onClick={() =>
                window.open(
                  'https://wa.me/send?l=pt&phone=5511947043137&text=%22Olá,%20quero%20conhecer%20a%20Anchor%20Experiences!%20Podem%20me%20ajudar?%22'
                )
              }
            />
            <FaLinkedin
              className={socialIcon}
              onClick={() =>
                window.open('https://www.linkedin.com/company/anchorexperiences/posts/?feedView=all')
              }
            />
          </div>
          <p className={copyright}>Anchor Experiences © 2026 | All Rights Reserved</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
